import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { ScreenHeader, Surface, Button, IconButton, Badge, Divider, Icon, FeedbackMsg, inputCss, AlunoSearch, FormField } from '../components/ui';

const API_URL = process.env.REACT_APP_API_URL;
const MESES = ['', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

function LancarModal({ alunos, onClose, onSaved }) {
  const [form, setForm] = useState({ aluno_id: '', mes: '', ano: new Date().getFullYear(), valor: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  function handleSubmit(e) {
    e.preventDefault();
    if (!form.aluno_id || !form.mes || !form.valor) { setMsg('Preencha todos os campos.'); return; }
    setSaving(true);
    axios.post(`${API_URL}/lancar_pagamento`, form, { withCredentials: true })
      .then(() => { onSaved(); onClose(); })
      .catch(err => setMsg(err.response?.data?.erro || 'Erro ao lançar.'))
      .finally(() => setSaving(false));
  }

  const selectStyle = { height: 40, padding: '0 12px', fontSize: 14, background: 'var(--bg-sunken)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', fontFamily: 'inherit', outline: 'none', width: '100%' };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: '100%', maxWidth: 480, background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0', boxShadow: 'var(--shadow-lg)', animation: 'fadeSlideUp .25s cubic-bezier(.22,1,.36,1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--gap-3)', borderBottom: '1px solid var(--border-hairline)' }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Lançar pagamento</div>
          <IconButton icon="close" onClick={onClose} variant="ghost"/>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: 'var(--gap-3)', display: 'flex', flexDirection: 'column', gap: 'var(--gap-2)' }}>
          <FormField label="Aluno">
            <AlunoSearch
              alunos={alunos}
              value={form.aluno_id}
              onChange={id => setForm(f => ({ ...f, aluno_id: id }))}
            />
          </FormField>
          <div style={{ display: 'flex', gap: 'var(--gap-2)' }}>
            <div style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 5 }}>
              <label style={{ fontSize: 12, fontWeight: 540, color: 'var(--text-secondary)' }}>Mês</label>
              <select value={form.mes} onChange={e => setForm(f => ({ ...f, mes: e.target.value }))} style={selectStyle} required>
                <option value="">Selecione</option>
                {MESES.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
              <label style={{ fontSize: 12, fontWeight: 540, color: 'var(--text-secondary)' }}>Ano</label>
              <input type="number" value={form.ano} onChange={e => setForm(f => ({ ...f, ano: e.target.value }))} min={2023} max={2100} style={inputCss} required/>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label style={{ fontSize: 12, fontWeight: 540, color: 'var(--text-secondary)' }}>Valor (R$)</label>
            <input type="number" step="0.01" min={0} value={form.valor} onChange={e => setForm(f => ({ ...f, valor: e.target.value }))} style={inputCss} required/>
          </div>
          {msg && <FeedbackMsg msg={msg}/>}
          <Button variant="primary" size="lg" disabled={saving} style={{ width: '100%', marginTop: 4 }}>
            {saving ? 'Salvando…' : 'Lançar pagamento'}
          </Button>
        </form>
      </div>
    </div>
  );
}

function EditarModal({ pagamento, onClose, onSaved }) {
  const [form, setForm] = useState({ valor: pagamento.valor, mes: pagamento.mes, ano: pagamento.ano, status: pagamento.status });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [msg, setMsg] = useState('');

  const selectStyle = { height: 40, padding: '0 12px', fontSize: 14, background: 'var(--bg-sunken)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', fontFamily: 'inherit', outline: 'none', width: '100%' };

  function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    axios.put(`${API_URL}/pagamento/${pagamento.id}`, form, { withCredentials: true })
      .then(() => { onSaved(); onClose(); })
      .catch(err => setMsg(err.response?.data?.erro || 'Erro ao salvar.'))
      .finally(() => setSaving(false));
  }

  function handleDelete() {
    if (!window.confirm('Excluir este pagamento?')) return;
    setDeleting(true);
    axios.delete(`${API_URL}/pagamento/${pagamento.id}`, { withCredentials: true })
      .then(() => { onSaved(); onClose(); })
      .catch(err => setMsg(err.response?.data?.erro || 'Erro ao excluir.'))
      .finally(() => setDeleting(false));
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: '100%', maxWidth: 480, background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0', boxShadow: 'var(--shadow-lg)', animation: 'fadeSlideUp .25s cubic-bezier(.22,1,.36,1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--gap-3)', borderBottom: '1px solid var(--border-hairline)' }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Editar pagamento</div>
          <IconButton icon="close" onClick={onClose} variant="ghost"/>
        </div>
        <form onSubmit={handleSave} style={{ padding: 'var(--gap-3)', display: 'flex', flexDirection: 'column', gap: 'var(--gap-2)' }}>
          <div style={{ display: 'flex', gap: 'var(--gap-2)' }}>
            <div style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 5 }}>
              <label style={{ fontSize: 12, fontWeight: 540, color: 'var(--text-secondary)' }}>Mês</label>
              <select value={form.mes} onChange={e => setForm(f => ({ ...f, mes: Number(e.target.value) }))} style={selectStyle} required>
                {MESES.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
              <label style={{ fontSize: 12, fontWeight: 540, color: 'var(--text-secondary)' }}>Ano</label>
              <input type="number" value={form.ano} onChange={e => setForm(f => ({ ...f, ano: Number(e.target.value) }))} min={2023} max={2100} style={{ ...selectStyle, width: 'auto' }} required/>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label style={{ fontSize: 12, fontWeight: 540, color: 'var(--text-secondary)' }}>Valor (R$)</label>
            <input type="number" step="0.01" min={0} value={form.valor} onChange={e => setForm(f => ({ ...f, valor: e.target.value }))} style={selectStyle} required/>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label style={{ fontSize: 12, fontWeight: 540, color: 'var(--text-secondary)' }}>Status</label>
            <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} style={selectStyle}>
              <option value="pendente">Pendente</option>
              <option value="pago">Pago</option>
            </select>
          </div>
          {msg && <FeedbackMsg msg={msg}/>}
          <Button variant="primary" size="lg" disabled={saving} style={{ width: '100%', marginTop: 4 }}>
            {saving ? 'Salvando…' : 'Salvar alterações'}
          </Button>
          <Button type="button" variant="ghost" size="lg" disabled={deleting} onClick={handleDelete}
            style={{ width: '100%', color: 'var(--error, #e53e3e)' }}>
            {deleting ? 'Excluindo…' : 'Excluir pagamento'}
          </Button>
        </form>
      </div>
    </div>
  );
}

export default function Pagamentos() {
  const { tipo, usuarioId } = useAuth();
  const [alunos, setAlunos] = useState([]);
  const [alunoId, setAlunoId] = useState('');
  const [pagamentos, setPagamentos] = useState([]);
  const [msg, setMsg] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (tipo === 'professora') {
      axios.get(`${API_URL}/alunos_aprovados`, { withCredentials: true })
        .then(r => setAlunos(r.data)).catch(() => {});
    } else if (usuarioId) {
      carregar(usuarioId);
    }
  }, [tipo, usuarioId]); // eslint-disable-line react-hooks/exhaustive-deps

  function carregar(id) {
    setLoading(true);
    axios.get(`${API_URL}/pagamentos/${id}`, { withCredentials: true })
      .then(r => setPagamentos(r.data))
      .catch(() => setPagamentos([]))
      .finally(() => setLoading(false));
  }

  function quitar(pagId) {
    if (!window.confirm('Marcar como pago?')) return;
    axios.post(`${API_URL}/quitar_pagamento/${pagId}`, {}, { withCredentials: true })
      .then(() => {
        setMsg('Pagamento quitado!');
        const refId = alunoId || usuarioId;
        if (refId) carregar(refId);
        setTimeout(() => setMsg(''), 3000);
      })
      .catch(() => setMsg('Erro ao quitar.'));
  }

  const selectStyle = { height: 40, padding: '0 12px', fontSize: 14, flex: 1, background: 'var(--bg-sunken)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', fontFamily: 'inherit', outline: 'none' };

  return (
    <div className="mema-screen" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-3)', paddingBottom: 90 }}>
      <ScreenHeader title="Pagamentos" action={
        tipo === 'professora'
          ? <Button size="sm" variant="primary" icon="plus" onClick={() => setModalOpen(true)}>Lançar</Button>
          : null
      }/>

      {tipo === 'professora' && (
        <div style={{ padding: '0 var(--gap-3)' }}>
          <Surface padded>
            <div style={{ fontSize: 12, fontWeight: 540, color: 'var(--text-tertiary)', marginBottom: 8 }}>Buscar pagamentos de aluno</div>
            <AlunoSearch
              alunos={alunos}
              value={alunoId}
              onChange={id => { setAlunoId(id); if (id) carregar(id); }}
              placeholder="Buscar aluno…"
            />
          </Surface>
        </div>
      )}

      <div style={{ padding: '0 var(--gap-3)' }}>
        {msg && <div style={{ marginBottom: 'var(--gap-2)' }}><FeedbackMsg msg={msg}/></div>}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 'var(--gap-4)', color: 'var(--text-tertiary)', fontSize: 13 }}>Carregando…</div>
        ) : pagamentos.length === 0 ? (
          <Surface padded style={{ textAlign: 'center', padding: 'var(--gap-4)' }}>
            <Icon name="payment" size={28} style={{ margin: '0 auto 8px', display: 'block', opacity: 0.35, color: 'var(--text-tertiary)' }}/>
            <div style={{ fontSize: 14, fontWeight: 540, color: 'var(--text-primary)' }}>
              {tipo === 'professora' ? 'Selecione um aluno para ver.' : 'Nenhum pagamento encontrado.'}
            </div>
          </Surface>
        ) : (
          <Surface>
            {pagamentos.map((p, i) => (
              <React.Fragment key={p.id}>
                {i > 0 && <Divider/>}
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gap-2)', padding: 'var(--pad-card)' }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 'var(--radius-md)', flexShrink: 0,
                    background: p.status === 'pago' ? 'color-mix(in oklch, var(--success) 12%, transparent)' : 'color-mix(in oklch, var(--warn) 14%, transparent)',
                    color: p.status === 'pago' ? 'var(--success)' : 'var(--warn)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon name="payment" size={18}/>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                      {MESES[p.mes]} / {p.ano}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                      <span style={{ fontFamily: '"Geist Mono", monospace', fontSize: 13, color: 'var(--text-primary)' }}>
                        R$ {Number(p.valor).toFixed(2)}
                      </span>
                      <Badge tone={p.status === 'pago' ? 'success' : 'warn'}>
                        {p.status === 'pago' ? 'Pago' : 'Pendente'}
                      </Badge>
                    </div>
                    {p.data_pagamento && (
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                        Pago em {p.data_pagamento}
                      </div>
                    )}
                  </div>
                  {tipo === 'professora' && (
                    <div style={{ display: 'flex', gap: 6 }}>
                      {p.status === 'pendente' && (
                        <Button size="sm" variant="soft" onClick={() => quitar(p.id)}>Quitar</Button>
                      )}
                      <IconButton icon="edit" size={32} variant="ghost" label="Editar" onClick={() => setEditando(p)}/>
                    </div>
                  )}
                </div>
              </React.Fragment>
            ))}
          </Surface>
        )}
      </div>

      {modalOpen && (
        <LancarModal alunos={alunos} onClose={() => setModalOpen(false)}
          onSaved={() => { if (alunoId) carregar(alunoId); }}/>
      )}
      {editando && (
        <EditarModal pagamento={editando} onClose={() => setEditando(null)}
          onSaved={() => { const refId = alunoId || usuarioId; if (refId) carregar(refId); }}/>
      )}
    </div>
  );
}
